/**
 * Centralized Error Handler
 * Captures, logs, and reports errors across the application
 * Copyright 2024 Delanoe Pirard / Aedelon - Apache 2.0
 */

import { eventBus } from './eventBus.js';

class ErrorHandler {
  constructor() {
    this.isInitialized = false;
    this.errorLog = [];
    this.maxLogSize = 50;
  }

  /**
   * Initialize global error handlers
   */
  init() {
    if (this.isInitialized) return;

    // Catch unhandled errors
    window.addEventListener('error', (event) => {
      this.handle(event.error || new Error(event.message), {
        type: 'uncaught',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
    });

    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.handle(event.reason, { type: 'unhandledrejection' });
    });

    this.isInitialized = true;
  }

  /**
   * Handle an error
   * @param {Error|string} error - The error to handle
   * @param {Object} context - Additional context
   */
  handle(error, context = {}) {
    const errorInfo = this.normalize(error, context);

    // Log to internal buffer
    this.log(errorInfo);

    // Console output (development)
    this.logToConsole(errorInfo);

    // Track in analytics (production)
    this.trackError(errorInfo);

    // Emit event for components to react
    eventBus.emit('error:occurred', errorInfo);

    return errorInfo;
  }

  /**
   * Normalize error into consistent format
   */
  normalize(error, context = {}) {
    const timestamp = new Date().toISOString();

    if (error instanceof Error) {
      return {
        message: error.message,
        name: error.name,
        stack: error.stack,
        timestamp,
        ...context
      };
    }

    return {
      message: String(error),
      name: 'Error',
      stack: null,
      timestamp,
      ...context
    };
  }

  /**
   * Add to internal log
   */
  log(errorInfo) {
    this.errorLog.push(errorInfo);

    // Keep log size bounded
    if (this.errorLog.length > this.maxLogSize) {
      this.errorLog.shift();
    }
  }

  /**
   * Console output with formatting
   */
  logToConsole(errorInfo) {
    const style = 'color: #EF4444; font-weight: bold;';

    console.group('%c[ErrorHandler]', style);
    console.error('Message:', errorInfo.message);
    if (errorInfo.type) console.log('Type:', errorInfo.type);
    if (errorInfo.stack) console.log('Stack:', errorInfo.stack);
    if (errorInfo.filename) console.log('File:', `${errorInfo.filename}:${errorInfo.lineno}:${errorInfo.colno}`);
    console.groupEnd();
  }

  /**
   * Track error in analytics
   */
  trackError(errorInfo) {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'exception', {
        description: errorInfo.message,
        fatal: errorInfo.type === 'uncaught'
      });
    }
  }

  /**
   * Wrap a function with error handling
   * @param {Function} fn - Function to wrap
   * @param {Object} context - Context to include in error reports
   * @returns {Function} Wrapped function
   */
  wrap(fn, context = {}) {
    return (...args) => {
      try {
        const result = fn(...args);

        // Handle async functions
        if (result instanceof Promise) {
          return result.catch((error) => {
            this.handle(error, { ...context, async: true });
            throw error; // Re-throw for caller to handle
          });
        }

        return result;
      } catch (error) {
        this.handle(error, context);
        throw error;
      }
    };
  }

  /**
   * Create a safe version of a function that doesn't throw
   * @param {Function} fn - Function to wrap
   * @param {*} fallback - Value to return on error
   * @returns {Function} Safe function
   */
  safe(fn, fallback = null) {
    return (...args) => {
      try {
        const result = fn(...args);

        if (result instanceof Promise) {
          return result.catch((error) => {
            this.handle(error, { safe: true });
            return fallback;
          });
        }

        return result;
      } catch (error) {
        this.handle(error, { safe: true });
        return fallback;
      }
    };
  }

  /**
   * Assert a condition, throw if false
   * @param {boolean} condition
   * @param {string} message
   */
  assert(condition, message) {
    if (!condition) {
      const error = new Error(`Assertion failed: ${message}`);
      this.handle(error, { type: 'assertion' });
      throw error;
    }
  }

  /**
   * Get recent errors
   */
  getRecentErrors(count = 10) {
    return this.errorLog.slice(-count);
  }

  /**
   * Clear error log
   */
  clearLog() {
    this.errorLog = [];
  }
}

// Singleton instance
export const errorHandler = new ErrorHandler();
